import { useEffect, useState } from 'react';

import { LoadingAnimations } from '../enum/LoadingAnimation';
import { AnimationData, getAnimation } from '../utils/loader';

interface ILoadedAnimation {
  type: LoadingAnimations;
  data: AnimationData;
}

/**
 * Fetches the tenant's loading animation on demand.
 *
 * `enabled` is what keeps this honest: the animations are separate chunks now, so requesting one
 * while the company is still resolving would download the default and then immediately download the
 * real one, and requesting one for a hidden overlay would download it for a screen nobody sees.
 *
 * A company with no `loadingAnimation` still gets the default - the field is absent on older
 * tenants and on partial responses, and the callers no longer apply that fallback themselves.
 *
 * The resolved type is kept alongside the data so a result can only ever be returned for the type
 * currently being asked for. Without that, changing type would keep showing the previous animation
 * while the new chunk loaded, and would keep showing it forever if that chunk failed.
 *
 * Returns `null` until it resolves, and stays `null` when the chunk fails: callers render their
 * loading text either way, so a missing animation degrades to a plain message, never a blank screen.
 */
const useAnimation = (
  type: LoadingAnimations | undefined,
  enabled = true,
): AnimationData | null => {
  const [loaded, setLoaded] = useState<ILoadedAnimation | null>(null);

  const effectiveType = type ?? LoadingAnimations.DEFAULT;

  useEffect(() => {
    if (!enabled) return undefined;

    // Guards against applying a result that arrived after the type changed or the component went
    // away, which would otherwise warn about a state update on an unmounted component.
    let current = true;

    getAnimation(effectiveType)
      .then((data) => {
        if (current) setLoaded({ type: effectiveType, data });
      })
      .catch((error) => {
        console.error('failed to load the loading animation', error);
      });

    return () => {
      current = false;
    };
  }, [effectiveType, enabled]);

  return enabled && loaded?.type === effectiveType ? loaded.data : null;
};

export default useAnimation;
